import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Clock, BadgeCheck, Heart, ExternalLink, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import ProductCard from '../components/ProductCard';
import ShopReviewsSection from '../components/ShopReviewsSection';
import ShareButton from '../components/ShareButton';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { location: userLocation } = useLocation();
  const { isAuthenticated } = useAuth();
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-GB' : 'it-IT';
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  const fetchShop = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      const response = await axios.get(`${API}/shops/${id}?${params.toString()}`);
      setShop(response.data);
    } catch (error) {
      console.error('Error fetching shop:', error);
    }
  }, [id, userLocation]);

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      const response = await axios.get(`${API}/shops/${id}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [id, userLocation]);

  const checkFavorite = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/favorites/check/${id}`, { withCredentials: true });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      // Guest users get 401 here - expected, not an error
      console.debug('checkFavorite skipped (likely guest):', error.message);
    }
  }, [id]);

  useEffect(() => {
    fetchShop();
    fetchProducts();
    checkFavorite();
  }, [fetchShop, fetchProducts, checkFavorite]);

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/negoziante');
      return;
    }

    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`, { withCredentials: true });
        setIsFavorite(false);
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, { withCredentials: true });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const formatDistance = (km) => {
    if (km === null || km === undefined) return null;
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(1)} km`;
  };

  const handleNavigate = () => {
    if (shop?.lat && shop?.lng) {
      const dest = `${shop.lat},${shop.lng}`;
      const origin = userLocation?.lat && userLocation?.lng
        ? `&origin=${userLocation.lat},${userLocation.lng}`
        : '';
      const url = `https://www.google.com/maps/dir/?api=1${origin}&destination=${dest}&travelmode=driving`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <Store className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="font-outfit font-semibold text-xl text-slate-700 mb-2">{t('shop.not_found')}</h2>
        <Button onClick={() => navigate(-1)} variant="outline">
          {t('common.goBack')}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="shop-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-6 h-6 text-slate-700" />
          </button>
          <h1 className="font-outfit font-semibold text-lg text-slate-800 truncate max-w-[200px]">
            {shop.nome}
          </h1>
          <div className="flex items-center gap-1">
            <ShareButton title={shop.nome} testId="shop-share" variant="icon" />
            <button
              onClick={toggleFavorite}
              disabled={favoriteLoading}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="favorite-button"
            >
              <Heart 
                className={`w-6 h-6 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-slate-400'}`} 
              />
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 py-4 max-w-7xl mx-auto">
        {/* Shop info card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6">
          <div className="flex items-start gap-4">
            {/* Logo */}
            {shop.logo ? (
              <img src={shop.logo} alt={shop.nome} className="w-16 h-16 rounded-xl object-cover" />
            ) : (
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                <Store className="w-8 h-8 text-blue-600" />
              </div>
            )}

            <div className="flex-1">
              {/* Name with verified badge */}
              <div className="flex items-center gap-2">
                <h2 className="font-outfit font-bold text-xl text-slate-800">{shop.nome}</h2>
                {shop.email_verified && (
                  <span className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    <BadgeCheck className="w-3.5 h-3.5" />
                    {t('shop.verified')}
                  </span>
                )}
              </div>

              {/* Description / About */}
              {shop.descrizione && (
                <p
                  className="text-slate-600 text-xs italic mt-1 line-clamp-3"
                  data-testid="shop-descrizione-public"
                >
                  {shop.descrizione}
                </p>
              )}

              {/* Address */}
              <p className="text-slate-600 text-sm mt-1">
                {shop.indirizzo}, {shop.citta}
              </p>

              {/* Distance & Open status */}
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {shop.distanza !== undefined && (
                  <span className="flex items-center gap-1 text-sm text-slate-500">
                    <MapPin className="w-4 h-4" />
                    {formatDistance(shop.distanza)}
                  </span>
                )}
                {shop.is_open !== null && (
                  <span className={`flex items-center gap-1 text-sm font-medium ${
                    shop.is_open ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    <Clock className="w-4 h-4" />
                    {shop.is_open
                      ? t('product.open_now')
                      : shop.next_opening
                        ? (shop.next_opening.toLowerCase().startsWith('chiuso')
                            ? shop.next_opening
                            : t('product.opens_at', { time: shop.next_opening }))
                        : t('product.closed')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Temporary closure banner */}
          {shop.closure_until && new Date(shop.closure_until) > new Date() && (
            <div
              className="mt-3 bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-center gap-2"
              data-testid="closure-banner"
            >
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <strong>{t('shop.closure_title')}</strong> · {t('shop.closure_reopening', {
                  date: new Date(shop.closure_until).toLocaleDateString(locale, {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                  }),
                })}
              </p>
            </div>
          )}

          {/* Temporary locations (ambulant/mixed) */}
          {shop.temporary_locations && shop.temporary_locations.length > 0 && (
            <div className="mt-3" data-testid="temp-locations-public">
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <MapPin className="w-4 h-4 text-blue-600" /> {t('shop.upcoming_stops')}
              </h3>
              <ul className="space-y-2">
                {shop.temporary_locations
                  .filter((loc) => new Date(loc.al_giorno) >= new Date(new Date().toDateString()))
                  .slice(0, 5)
                  .map((loc) => (
                    <li key={loc.id} className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
                      <p className="font-medium text-slate-800">
                        {loc.indirizzo || loc.citta || t('shop.on_map_simple')}
                      </p>
                      {loc.indirizzo && loc.citta && (
                        <p className="text-xs text-slate-600">{loc.citta}</p>
                      )}
                      <p className="text-xs text-blue-700 mt-1">
                        {t('shop.from_date_to_date', {
                          from_date: new Date(loc.dal_giorno).toLocaleDateString(locale),
                          from_time: loc.dal_orario,
                          to_date: new Date(loc.al_giorno).toLocaleDateString(locale),
                          to_time: loc.al_orario,
                        })}
                      </p>
                      {loc.note && <p className="text-xs text-slate-500 italic mt-1">{loc.note}</p>}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Photo gallery */}
          {shop.gallery_photos && shop.gallery_photos.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="shop-gallery">
              {shop.gallery_photos.map((url, idx) => (
                <div
                  key={`${url}-${idx}`}
                  className="aspect-square rounded-xl overflow-hidden bg-slate-100"
                >
                  <img
                    src={url}
                    alt={`${shop.nome} ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Opening hours */}
          {shop.orari_apertura && Object.keys(shop.orari_apertura).length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <h3 className="font-semibold text-sm text-slate-700 mb-2">{t('shop.opening_hours')}</h3>
              <div className="grid grid-cols-2 gap-1 text-sm">
                {['lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi', 'sabato', 'domenica'].map(day => (
                  <div key={day} className="flex justify-between">
                    <span className="text-slate-500">{t(`shop.days.${day}`)}</span>
                    <span className="text-slate-700">{shop.orari_apertura[day] || t('product.closed')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigate button */}
          <Button
            onClick={handleNavigate}
            className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3"
            data-testid="navigate-button"
          >
            <MapPin className="w-5 h-5 mr-2" />
            {t('shop.directions')}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Products */}
        <div>
          <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-4">
            {t('shop.products_title')} ({products.length})
          </h3>

          {products.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl">
              <Store className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{t('shop.no_products')}</p>
            </div>
          )}
        </div>

        {/* Reviews */}
        <ShopReviewsSection shopId={shop.id} shopOwnerId={shop.user_id} />
      </main>

      <BottomNav />
    </div>
  );
}
